import type {
    TacticalPlanRuleRequestType,
    TacticalPlanRuleResponseType,
} from './TacticalPlanRuleApi';
import { TacticalPlanRequestSchema } from './TacticalPlanApi';
import { TacticalPlanRuleRequestSchema } from './TacticalPlanRuleApi';
import { explodeAndOr } from '../telematics/components/FilterByParameters/FilterManager';
import { extractFieldsFromB } from '../../utilities/SchemaManager';
import type { ZoneListErrorType } from '../Zones/ZoneListTypes';

export const getEmptyDefaultRule = (tacticalPlanId: number): TacticalPlanRule => {
    const defaultTacticalPlanRule: TacticalPlanRuleResponseType = {
        id: null,
        tactical_plan_id: tacticalPlanId,
        tactical_plan_rule_type_id: 1,
        name: '', // FIXME: move it to translation
        priority: 0,
        details: {
            filter: {},
            vehicle_id: null,
            action: {
                type: 'map',
                map_id: null, // ryba (kopia) // FIXME: change to null --- as new map
            },
            errors: []
        },
        created: '0000-00-00 00:00:00',
        updated: '0000-00-00 00:00:00',
        revision: '',
        auto_confirm: false, // false by default due to problems with adhocs https://matkomunikator.automapa.pl:801/automapa/pl/7wg5nbqoktffbdiweqhetrhhah
        enabled: true,
    };
    return new TacticalPlanRule(defaultTacticalPlanRule, true);
};

export type RuleActionType = 'map' | 'vehicle' | 'unset';

export class TacticalPlanRule {
    private rule: TacticalPlanRuleResponseType;
    private __id: string;
    private isNew: boolean;

    constructor(rule: TacticalPlanRuleResponseType, isNew: boolean) {
        this.rule = rule;
        this.fixRuleUnsetToBeAsVehicle();
        this.isNew = isNew;
        this.__id = Math.random().toString(36);
    }

    public getTacticalPlanId(): number | null {
        return this.rule.tactical_plan_id;
    }

    private fixRuleUnsetToBeAsVehicle() {
        if (this.rule.details.action.type === 'unset') {
            this.rule.details.action = {
                type: 'vehicle',
                vehicle_id: null,
            };
        }
    }

    public getRule() {
        return this.rule;
    }

    public setInnerId(id: string) {
        this.__id = id;
    }

    public setRule(rule: TacticalPlanRuleResponseType, isNew: boolean) {
        this.rule = rule;
        this.fixRuleUnsetToBeAsVehicle();
        this.isNew = isNew;
    }

    public setVehicleId(vehicleId: number | null) {
        switch (this.rule.details.action.type) {
            case 'map': {
                this.rule.details.vehicle_id = vehicleId;
                break;
            }
            case 'vehicle': {
                this.rule.details.action.vehicle_id = vehicleId;
                delete this.rule.details.action.map_id;
                break;
            }
            case 'unset': {
                throw new Error('Cannot set vehicle id for unset action type');
            }
        }
    }

    public setMapId(mapId: number | null) {
        this.rule.details.action.type = 'map';
        this.rule.details.action.map_id = mapId;
        delete this.rule.details.action.vehicle_id;
    }

    public getMapId(): number | null {
        return this.rule.details.action.map_id;
    }

    public getPriority(): number {
        return this.rule.priority;
    }

    public selectActionType(actionType: RuleActionType) {
        if (actionType !== this.rule.details.action.type) {
            this.rule.details.action.type = actionType;
            switch (actionType) {
                case 'map': {
                    this.setMapId(null);
                    break;
                }
                case 'vehicle': {
                    this.setVehicleId(null);
                    break;
                }
                case 'unset': {
                    delete this.rule.details.action.vehicle_id;
                    delete this.rule.details.action.map_id;
                    break;
                }
                default: {
                    throw new Error('Unknown action type: ' + actionType);
                }
            }
        }
    }

    // private extractFieldsFromB(objB: any, schemaA: Record<string, any>): Record<string, any> {
    //     const result: Record<string, any> = {};
    //
    //     for (const key in schemaA) {
    //         if (objB.hasOwnProperty(key)) {
    //             result[key] = objB[key];
    //         }
    //     }
    //
    //     return result;
    // }

    public getRuleReadyToRequest() {
        let output = extractFieldsFromB(this.rule, TacticalPlanRuleRequestSchema);
        TacticalPlanRuleRequestSchema.parse(output);
        return output as TacticalPlanRuleRequestType;
    }

    public getId(): number | null {
        return this.rule.id;
    }

    public getInnerId(): string {
        return this.__id;
    }

    public isNewRule(): boolean {
        return this.isNew;
    }

    public setNewRule(isNew: boolean) {
        this.isNew = isNew;
    }

    public isAlwaysLast() {
        return this.rule.priority === -1;
    }

    public setAlwaysLast() {
        this.rule.priority = -1;
    }

    public hasEmptyFilter() {
        let { queries, __andOrSelection } = explodeAndOr(this.rule.details.filter);
        return this.rule.details.filter === null || Object.keys(queries).length === 0;
    }

    public getName() {
        return this.rule.name;
    }

    public setName(name: string) {
        this.rule.name = name;
    }

    public getVehicleId(): number | null {
        switch (this.rule.details.action.type) {
            case 'map': {
                return this.rule.details.vehicle_id;
            }
            case 'vehicle': {
                return this.rule.details.action?.vehicle_id || null;
            }
            case 'unset': {
                return null;
            }
            default: {
                throw new Error('Unknown action type: ' + this.rule.details.action.type);
            }
        }
    }

    public getType() {
        return this.rule.details.action.type;
    }

    public getAutoConfirm() {
        return this.rule.auto_confirm;
    }

    public setAutoConfirm(autoConfirm: boolean) {
        this.rule.auto_confirm = autoConfirm;
    }

    public setErrors(errors: ZoneListErrorType[]) {
        console.log('setErrors', errors);
        this.rule.details.errors = errors;
    }

    public getErrors() {
        return this.rule.details.errors;
    }

    public getEnabled(): boolean {
        return this.rule.enabled ?? true;
    }

    public setEnabled(enabled: boolean) {
        this.rule.enabled = enabled;
    }
}
